/* eslint-disable no-extra-boolean-cast */
/* eslint-disable react/jsx-curly-brace-presence */
import { useMutation } from "@apollo/client";
import { Button, Drawer, Form, Select, message } from "antd";
import React, { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { UserContext } from "../../../amplify/authenticator-provider";
import { getMobileView } from "../../../redux/slices/isMobile";
import { MOVE_LEAD_LOCATION } from "../../../graphql/mutation";
import useFetchUsers, { USER_QUERIES } from "../../../hooks/useFetchUsers";
import ShiftIcon from "../../../icons/svg/shift.svg";

const handleOwnerName = opprUsers => {
  return (
    <div className="flex flex-wrap gap-1 text-sm text-grey-700 font-[400]">
      {(opprUsers || []).map((item, i) => {
        const companyRole = item?.user?.companyRole?.name;
        const userType = item?.userType?.name;

        let roleInfo = "";
        if (companyRole && userType) {
          roleInfo = ` (${companyRole} - ${userType})`;
        } else if (companyRole) {
          roleInfo = ` (${companyRole})`;
        } else if (userType) {
          roleInfo = ` (${userType})`;
        }

        return (
          <p key={`owner0key-${i}`}>{`${item?.user?.firstName} ${
            item?.user?.lastName || ""
          }${roleInfo}`}</p>
        );
      })}
    </div>
  );
};
const ShiftLeadDrawer = ({ open, onClose, refetchLeadDetails, refetchLeads, setSelectedLead }) => {
  const { user } = useContext(UserContext);
  const { selectedLead } = useSelector(state => state.lead);
  const isMobileView = useSelector(state => getMobileView(state));
  const [form] = Form.useForm();
  const [moveLeadLocation, { loading: moveLeadLocationLoader }] = useMutation(MOVE_LEAD_LOCATION);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  // Uses setting flag (isActiveUserRendering) to determine active or all users
  const { users: userList } = useFetchUsers(null, null, !user?.company?.id || !selectedLocationId, {
    locationIds: selectedLocationId ? [+selectedLocationId] : [],
    query: USER_QUERIES.ACTIVITY_CENTER,
    fetchPolicy: "cache-and-network",
  });

  const handleClose = () => {
    form.resetFields();
    setSelectedLocationId(null);
    onClose();
  };

  const handleShift = async values => {
    try {
      const res = await moveLeadLocation({
        variables: {
          leadId: +selectedLead?.id,
          locationId: +values.locationId,
          assignedUserId: values.userId ? +values.userId : undefined,
        },
      });

      if (res?.data?.moveLeadLocation?.statusCode === 200) {
        message.success("Lead shifted successfully");

        // Clear the selected lead since it's been moved to a different location
        if (setSelectedLead) {
          setSelectedLead({});
        }

        // Refetch the lead list for the current location to remove the shifted lead
        if (refetchLeads) {
          refetchLeads();
        }

        handleClose();
      } else {
        message.error(res?.data?.moveLeadLocation?.message || "Failed to shift lead");
      }
    } catch (error) {
      message.error("An error occurred while shifting the lead");
    }
  };

  const primaryPhone =
    selectedLead?.phoneNumbers?.find(item => item?.isPrimary)?.phone ||
    selectedLead?.phoneNumbers?.find(item => item.lookupType === "mobile")?.phone ||
    "";

  const primaryEmail = selectedLead?.emails?.find(item => item?.isPrimary)?.email || "N/A";

  const locationOptions =
    user?.locations?.map(item => ({
      label: item.location.title,
      value: item.location.id,
    })) || [];

  // Get users for the selected location
  const getUserOptions = () => {
    return (userList || []).map(u => ({
      label: u.fullName,
      value: u.id,
    }));
  };

  const userOptions = getUserOptions();
  return (
    <Drawer
      open={open}
      className="normal-popup"
      width={isMobileView ? "100%" : "30%"}
      title="Shift lead"
      onClose={handleClose}
      footer={
        <div className="flex-row-centered gap-2 items-center">
          <Button loading={moveLeadLocationLoader} type="primary" onClick={() => form.submit()}>
            Shift lead
          </Button>
          <Button onClick={handleClose}>Cancel</Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="bg-gray-100 p-4 rounded-lg">
          <h3 className="text-sm font-semibold mb-3">Lead information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600 mb-1">Name</p>
              <p className="text-sm font-medium">
                {selectedLead?.firstName || selectedLead?.lastName
                  ? `${selectedLead?.firstName || ""} ${selectedLead?.lastName || ""}`.trim()
                  : "N/A"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Phone</p>
              <p className="text-sm font-medium">
                {primaryPhone ? formatPhoneNumberIntl(primaryPhone) : "N/A"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <p className="text-xs text-gray-600 mb-1">Email</p>
              <p className="text-sm font-medium break-all">{primaryEmail}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">No. of opportunity</p>
              <p className="text-sm font-medium">{selectedLead?.leadOpportunities?.length || 0}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <p className="text-xs text-gray-600 mb-1">Current location</p>
              <p className="text-sm font-medium">{selectedLead?.location?.title || "N/A"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Current owner</p>
              <p className="text-sm font-medium">
                {handleOwnerName(selectedLead?.leadUsers)} (Primary Assigned)
              </p>
            </div>
          </div>
        </div>

        {/* Shift To Section */}
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-2 bg-white border-1 border-gray-200 rounded-full px-4 py-2">
            <img src={ShiftIcon} alt="shift" className="w-4 h-4" />
            <span className="text-sm font-medium text-gray-600">Shift to</span>
          </div>
        </div>

        {/* Form Section */}
        <div className="border-1 border-gray-200 rounded-lg p-4">
          <Form form={form} onFinish={handleShift} layout="vertical">
            <p className="text-sm font-medium mb-3">Select a location, then assign the owner</p>

            <Form.Item
              name="locationId"
              label="Select location"
              rules={[{ required: true, message: "Please select a location" }]}
            >
              <Select
                placeholder="Select location to shift"
                options={locationOptions}
                showSearch
                onChange={value => {
                  setSelectedLocationId(value);
                  form.setFieldsValue({ userId: undefined });
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item
              name="userId"
              label="Select owner"
              rules={[{ required: true, message: "Please select an owner" }]}
            >
              <Select
                placeholder="Select owner"
                options={userOptions}
                showSearch
                disabled={!selectedLocationId}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Form>
        </div>
      </div>
    </Drawer>
  );
};

export default ShiftLeadDrawer;
